// Pending forced acknowledgements that may not appear in the workflow thread list
// (e.g. requires_ack reminders filtered out of inbox threads).
import { useCallback, useEffect, useState } from "react";
import {
  acknowledgeOpsNotification,
  listOpsActionableAcknowledgements,
} from "@/lib/opsPaperworkClient";

type AckItem = {
  id: string;
  title?: string | null;
  body?: string | null;
  kind?: string | null;
};

export type WorkplaceActionableAcksPanelProps = {
  enabled: boolean;
  onAcknowledged?: () => void;
};

export function WorkplaceActionableAcksPanel({
  enabled,
  onAcknowledged,
}: WorkplaceActionableAcksPanelProps) {
  const [items, setItems] = useState<AckItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!enabled) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError("");
    const r = await listOpsActionableAcknowledgements();
    setLoading(false);
    if (!r.ok) {
      setError(r.error || "Unable to load acknowledgements.");
      setItems([]);
      return;
    }
    const raw = (r.data && typeof r.data === "object" && "items" in r.data
      ? (r.data as { items?: unknown[] }).items
      : []) as AckItem[];
    setItems(
      (raw ?? [])
        .filter((row) => row && typeof row.id === "string")
        .map((row) => ({
          id: row.id,
          title: row.title ?? null,
          body: row.body ?? null,
          kind: row.kind ?? null,
        })),
    );
  }, [enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onAck(id: string) {
    setBusyId(id);
    setError("");
    const r = await acknowledgeOpsNotification({ id });
    setBusyId(null);
    if (!r.ok) {
      setError(r.error || "Unable to acknowledge.");
      return;
    }
    setItems((prev) => prev.filter((i) => i.id !== id));
    onAcknowledged?.();
  }

  if (!enabled) return null;
  if (!loading && items.length === 0 && !error) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/30">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-100">
        Needs acknowledgement
      </p>
      {loading ? (
        <p className="mt-1 text-[11px] text-amber-800/80 dark:text-amber-200/80">Loading…</p>
      ) : null}
      {error ? <p className="mt-1 text-[11px] text-red-600 dark:text-red-300">{error}</p> : null}
      <ul className="mt-1.5 space-y-1.5">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex items-start justify-between gap-2 rounded-md bg-white/70 px-2 py-1.5 dark:bg-zinc-900/50"
          >
            <div className="min-w-0">
              <div className="text-[11px] font-medium text-gray-900 dark:text-gray-100">
                {item.title || "Notification"}
              </div>
              {item.body ? (
                <div className="line-clamp-2 text-[10px] text-gray-600 dark:text-gray-400">{item.body}</div>
              ) : null}
              {item.kind ? (
                <div className="text-[9px] text-gray-500 dark:text-gray-500">{item.kind}</div>
              ) : null}
            </div>
            <button
              type="button"
              disabled={busyId === item.id}
              onClick={() => void onAck(item.id)}
              className="shrink-0 rounded-md bg-amber-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
            >
              {busyId === item.id ? "…" : "Acknowledge"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
